import React, { FC, ReactNode, useMemo } from 'react';

import { css } from '@emotion/react';

import SortingCell from '@components/Common/SortingCell';

import useQueryString from '@hooks/useQueryString';

import { palette } from '@styles/theme';

import {
  SumColumnConfig,
  TotalRowConfig,
  addTotalRow,
  extractSumColumnsFromHeader,
} from '@utils/tableUtils';

interface Props {
  header: any[];
  data: any[];
  isDetailPage?: boolean;
  handleDetailPage?: (id: string) => void;
  sortByDefault?: string;
  sortByFrom?: string;
  sortByTo?: string;
  cellPadding?: number;
  bottomComponent?: ReactNode;
  noDataComponent?: ReactNode;
  /** 합계를 표시할 컬럼 설정 배열 */
  sumColumns?: SumColumnConfig[];
  /** 합계 행 설정 */
  totalRowConfig?: TotalRowConfig;
}

const Table: FC<Props> = ({
  header,
  data,
  isDetailPage = false,
  handleDetailPage,
  sortByDefault = 'no',
  sortByFrom,
  sortByTo,
  cellPadding = 10,
  bottomComponent,
  noDataComponent,
  sumColumns,
  totalRowConfig,
}) => {
  const { sort, sortBy, setQuery } = useQueryString({ sortBy: sortByDefault });

  const handleSortBy = (value: string) => {
    if (sortByFrom && sortByTo) {
      return sortByFrom === value ? sortByTo : value;
    } else {
      return value;
    }
  };

  // 합계 행이 자동으로 추가되어야 하는 경우 처리
  const displayData = useMemo(() => {
    // sumColumns가 명시적으로 전달되지 않으면 header에서 자동 추출
    const finalSumColumns =
      sumColumns || extractSumColumnsFromHeader(header || []);

    if (finalSumColumns && finalSumColumns.length > 0) {
      // 이미 합계 행이 있는지 확인
      const hasTotalRow = data?.some((row) => row?.isTotalRow === true);
      if (!hasTotalRow) {
        return addTotalRow(data || [], finalSumColumns, totalRowConfig);
      }
    }
    return data || [];
  }, [data, sumColumns, totalRowConfig, header]);

  return (
    <table css={container}>
      <colgroup>
        {header.map((h: any) => (
          <col key={h?.value} css={col(h?.width)} />
        ))}
      </colgroup>

      <thead>
        <tr>
          {header.map((h, i: number) => (
            <th
              key={h?.value}
              css={[
                ths,
                i === 0 && { borderTopLeftRadius: '5px' },
                i === header.length - 1 && { borderTopRightRadius: '5px' },
                h?.noWrap && { whiteSpace: 'nowrap', wordBreak: 'keep-all' },
              ]}
            >
              {h?.isSorting ? (
                <div
                  css={[
                    {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                      cursor: 'pointer',
                    },
                  ]}
                  onClick={() =>
                    setQuery({
                      sortBy:
                        sort === 'asc' && sortBy === handleSortBy(h?.value)
                          ? undefined
                          : handleSortBy(h?.value),
                      sort:
                        sortBy === handleSortBy(h?.value)
                          ? sort === 'desc'
                            ? 'asc'
                            : undefined
                          : 'desc',
                    })
                  }
                >
                  {h?.label}
                  {h?.isSorting && (
                    <SortingCell
                      flag={handleSortBy(h?.value) === sortBy}
                      order={sort}
                    />
                  )}
                </div>
              ) : (
                h?.label
              )}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {displayData?.length === 0 ? (
          noDataComponent ? (
            noDataComponent
          ) : (
            <tr>
              <td
                colSpan={header?.length}
                css={{ textAlign: 'center', padding: '20px' }}
              >
                검색된 데이터가 없습니다.
              </td>
            </tr>
          )
        ) : (
          displayData?.map((row, rowIndex) => {
            const isTotalRow = row?.isTotalRow === true;
            const isLastRow = rowIndex === displayData.length - 1;

            return (
              <tr
                key={row?.id || rowIndex}
                css={[
                  isDetailPage && !isTotalRow && hoverFlag,
                  isTotalRow && totalRowStyle,
                ]}
                onClick={() => {
                  if (!isTotalRow && handleDetailPage) {
                    handleDetailPage(row?.id);
                  }
                }}
              >
                {header?.map((h, i: number) => {
                  const cellValue = row[h?.value];
                  const isStopPropagation = h?.stopPropagation;

                  return (
                    <td
                      key={h.value}
                      rowSpan={row?.rowSpan?.[h?.value]}
                      css={[
                        tbodyTd,
                        {
                          paddingTop: cellPadding,
                          paddingBottom: cellPadding,
                          paddingLeft: 6,
                          paddingRight: 6,
                        },
                        isLastRow &&
                          i === 0 && { borderBottomLeftRadius: '5px' },
                        isLastRow &&
                          i === header?.length - 1 && {
                            borderBottomRightRadius: '5px',
                          },
                        h.textAlign && { textAlign: h?.textAlign },
                        h.borderRight && { borderRight: '1px solid #e0e0e0' },
                        isTotalRow && {
                          fontWeight: 700,
                          backgroundColor: '#f9f9f9',
                        },
                      ]}
                      onClick={(e) => {
                        if (isStopPropagation) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {isTotalRow && cellValue === undefined ? (
                        ''
                      ) : isStopPropagation ? (
                        cellValue
                      ) : typeof cellValue === 'string' ||
                        typeof cellValue === 'number' ? (
                        <span css={selectableText}>{cellValue ?? '-'}</span>
                      ) : (
                        cellValue ?? '-'
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}
        {bottomComponent ? bottomComponent : null}
      </tbody>
    </table>
  );
};

export default Table;

const container = css`
  width: 100%;
  border: 0;
  border-spacing: 0;
  border-collapse: collapse;
  border-radius: 5px;
  border-style: hidden;
  box-shadow: 0 0 0 1px #e0e0e0;

  td > button {
    display: inline-flex;
  }
`;

const col = (width: string | null) => css`
  ${width !== null &&
  css`
    width: ${width};
드  `}
`;

const ths = css`
  font-weight: 700;
  word-break: break-all;
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  text-align: center;
  font-size: 14px;
  letter-spacing: -0.42px;
  height: 42px;
  box-sizing: border-box;
  background-color: #f2f2f2;
  color: #222222;
  vertical-align: middle;
`;

const tbodyTd = css`
  /* padding: 10px; */
  border: 1px solid #eee;
  text-align: center;
  font-size: 14px;
  box-sizing: border-box;
  height: 45px;
  background-color: #ffffff;
  vertical-align: middle;
  word-break: keep-all;
`;

const hoverFlag = css`
  &:hover {
    cursor: pointer;
    td {
      background-color: ${palette.gray1};
    }
  }
`;

const totalRowStyle = css`
  cursor: default;
  &:hover {
    td {
      background-color: #f9f9f9 !important;
    }
  }
`;

const selectableText = css`
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  display: inline-block;
  width: 100%;
`;
